import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { BubbleCanvas } from '@/components/intro/BubbleCanvas';
import { INTRO_TIMELINE, markIntroShown, shouldSkipIntro, type IntroPhase } from '@/lib/intro';
import { cn } from '@/lib/utils';

type IntroSplashProps = {
  onComplete: () => void;
};

export function IntroSplash({ onComplete }: IntroSplashProps) {
  const [phase, setPhase] = useState<IntroPhase>('logo');
  const [visible, setVisible] = useState(true);
  const finishedRef = useRef(false);
  const timersRef = useRef<number[]>([]);

  const finish = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    for (const id of timersRef.current) window.clearTimeout(id);
    timersRef.current = [];
    markIntroShown();
    setVisible(false);
    onComplete();
  }, [onComplete]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const { logoMs, bubblesMs, floodMs, revealMs } = INTRO_TIMELINE;
    const schedule = (fn: () => void, ms: number) => {
      const id = window.setTimeout(fn, ms);
      timersRef.current.push(id);
    };

    schedule(() => setPhase('bubbles'), logoMs);
    schedule(() => setPhase('flood'), logoMs + bubblesMs);
    schedule(() => setPhase('reveal'), logoMs + bubblesMs + floodMs);
    schedule(
      () => {
        setPhase('done');
        finish();
      },
      logoMs + bubblesMs + floodMs + revealMs,
    );

    return () => {
      for (const id of timersRef.current) window.clearTimeout(id);
      timersRef.current = [];
    };
  }, [finish]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [finish]);

  if (!visible) return null;

  const bursting = phase === 'flood' || phase === 'reveal';

  return (
    <div
      className={cn(
        'intro-splash fixed inset-0 z-[100] overflow-hidden',
        phase === 'reveal' && 'intro-splash--exit',
        phase === 'done' && 'intro-splash--gone',
      )}
      role="presentation"
      aria-hidden
    >
      <div className="intro-splash__bg absolute inset-0 bg-[#06080c]" />

      <BubbleCanvas active burst={bursting} />

      <div
        className={cn(
          'intro-splash__logo pointer-events-none absolute inset-0 flex flex-col items-center justify-center',
          (phase === 'flood' || phase === 'reveal') && 'intro-splash__logo--drown',
        )}
      >
        <p className="intro-splash__eyebrow mb-3 text-[10px] font-semibold uppercase tracking-[0.45em] text-[var(--color-muted)]">
          501 Entertainment
        </p>
        <h1 className="score intro-splash__title text-[clamp(4.5rem,22vw,9rem)] leading-none text-[var(--color-chalk)]">
          501
        </h1>
        <p className="mt-4 text-sm font-medium tracking-[0.35em] text-[var(--color-oche)] uppercase">Oche</p>
      </div>

      <div
        className={cn(
          'intro-splash__liquid pointer-events-none absolute inset-x-0 bottom-0',
          phase === 'flood' && 'intro-splash__liquid--rise',
          phase === 'reveal' && 'intro-splash__liquid--reveal',
        )}
        aria-hidden
      >
        <div className="intro-splash__wave intro-splash__wave--back" />
        <div className="intro-splash__wave intro-splash__wave--front" />
      </div>

      <div
        className={cn(
          'intro-splash__shine pointer-events-none absolute inset-0',
          phase === 'reveal' && 'intro-splash__shine--flash',
        )}
        aria-hidden
      />

      <button
        type="button"
        className="intro-splash__skip absolute right-4 bottom-4 z-10 rounded-md px-3 py-1.5 text-[11px] font-medium tracking-wide text-[var(--color-muted)] uppercase transition-colors hover:text-[var(--color-chalk)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-oche)]"
        onClick={finish}
      >
        Skip
      </button>
    </div>
  );
}

type IntroGateProps = {
  children: ReactNode;
};

/** Shows cinematic intro once per session; children render underneath during the sequence. */
export function IntroGate({ children }: IntroGateProps) {
  const [introComplete, setIntroComplete] = useState(shouldSkipIntro);

  return (
    <>
      {!introComplete && <IntroSplash onComplete={() => setIntroComplete(true)} />}
      <div className={cn(!introComplete && 'intro-app-pending', introComplete && 'oche-enter-fade')}>
        {children}
      </div>
    </>
  );
}
